import type { Product, ProductVariant, VariantForm } from '../types/product';
import { formatEuro } from './utils';

// Regole comuni su formati e prezzi, usate da schede, pagina prodotto,
// carrello e dashboard: un solo posto dove si decide cosa è in vendita.

const bySortOrder = (a: ProductVariant, b: ProductVariant) => a.sort_order - b.sort_order || a.grams - b.grams;

/** Formati visibili sul sito (attivi), in ordine di listino. */
export function activeVariants(product: Product): ProductVariant[] {
  return (product.product_variants ?? []).filter((v) => v.active).sort(bySortOrder);
}

/** Tutti i formati, anche disattivati, in ordine di listino (dashboard). */
export function allVariants(product: Product): ProductVariant[] {
  return [...(product.product_variants ?? [])].sort(bySortOrder);
}

export function isSoldOut(product: Product): boolean {
  return !activeVariants(product).some((v) => v.stock > 0);
}

/** Vasetti in vendita sul sito, tutti i formati insieme. */
export function totalStock(product: Product): number {
  return activeVariants(product).reduce((sum, v) => sum + v.stock, 0);
}

/** Prezzo più basso tra i formati attivi ("da € 6,00"), null se non ce ne sono. */
export function fromPrice(product: Product): number | null {
  const prices = activeVariants(product).map((v) => v.price);
  return prices.length > 0 ? Math.min(...prices) : null;
}

/** true se i formati hanno prezzi diversi: si mostra "da" davanti al prezzo. */
export function hasPriceRange(product: Product): boolean {
  return new Set(activeVariants(product).map((v) => v.price)).size > 1;
}

/** Prezzo da mostrare sulle schede: "da 6,00 €" se i formati costano diversamente. */
export function priceLabel(product: Product): string {
  const price = fromPrice(product);
  if (price === null) return '';
  return hasPriceRange(product) ? `da ${formatEuro(price)}` : formatEuro(price);
}

/** Formato proposto all'apertura: il primo disponibile, altrimenti il primo. */
export function defaultVariant(product: Product): ProductVariant | null {
  const variants = activeVariants(product);
  return variants.find((v) => v.stock > 0) ?? variants[0] ?? null;
}

/**
 * Formato da aggiungere al carrello senza chiedere: solo quando il miele ha
 * un unico formato (es. il favo). Con più formati il cliente deve scegliere.
 */
export function onlyVariant(product: Product): ProductVariant | null {
  const variants = activeVariants(product);
  return variants.length === 1 ? variants[0] : null;
}

/** Sconto reale: c'è solo se il prezzo pieno è più alto di quello di vendita. */
export function hasDiscount(variant: ProductVariant): boolean {
  return !!variant.original_price && variant.original_price > variant.price;
}

/** Nome mostrato in carrello, ordini ed email: "Miele Millefiori — 500 g". */
export function itemName(productName: string, variantLabel?: string | null): string {
  return variantLabel ? `${productName} — ${variantLabel}` : productName;
}

// ---------------------------------------------------------------------------
// Dashboard: form dei formati
// ---------------------------------------------------------------------------

export const newVariant = (label: string, grams: number, sort_order: number): VariantForm => ({
  label,
  grams,
  price: 0,
  original_price: null,
  discount_percent: null,
  stock: 0,
  inventory: 0,
  sort_order,
  active: true,
});

/** Errore da mostrare sopra i formati, null se è tutto a posto. */
export function validateVariants(variants: VariantForm[]): string | null {
  if (variants.filter((v) => v.active).length === 0) return 'Serve almeno un formato attivo.';
  const grams = new Set<number>();
  for (const v of variants) {
    const name = v.label.trim() || 'Un formato';
    if (!v.label.trim()) return 'Ogni formato deve avere un nome (es. 500 g).';
    if (!Number.isInteger(v.grams) || v.grams <= 0) return `${name}: indica il peso in grammi.`;
    if (grams.has(v.grams)) return `Ci sono due formati da ${v.grams} g.`;
    grams.add(v.grams);
    if (!(v.price > 0)) return `${name}: il prezzo deve essere maggiore di 0.`;
    if (v.discount_percent && (v.discount_percent < 0 || v.discount_percent > 100))
      return `${name}: lo sconto deve essere tra 0 e 100%.`;
    if (v.stock < 0 || v.inventory < 0) return `${name}: le quantità non possono essere negative.`;
    if (v.stock > v.inventory) return `${name}: non puoi mettere online più vasetti di quelli in magazzino.`;
  }
  return null;
}
